import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SparePartUsageTrendWidget = ({ canViewCost = false }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.sparePartUsageTrend || [];
    return rows.map((row) => ({
      date: new Date(row.date).toLocaleDateString(i18n.language || 'en-US', { month: 'short', day: 'numeric' }),
      quantityUsed: Number(row.quantityUsed) || 0,
      amountSpent: Number(row.amountSpent) || 0,
    }));
  }, [data, i18n.language]);

  const isEmpty = chartData.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.spare_part_usage_trend', 'Spare Part Usage Trend')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.spare_part_usage_trend.empty', 'No spare-part usage data to display.')}
      onRefresh={handleRefresh}
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(value, name, props) => {
              if (props?.dataKey === 'amountSpent') return [formatMoney(value, currency), name];
              return [value, name];
            }}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="quantityUsed"
            stroke="#8b5cf6"
            strokeWidth={2}
            name={t('business.inventory.quantity_used', 'Quantity Used')}
            dot={{ r: 4 }}
          />
          {canViewCost && (
            <Line
              type="monotone"
              dataKey="amountSpent"
              stroke="#ec4899"
              strokeWidth={2}
              name={t('business.inventory.amount_spent', 'Amount Spent')}
              dot={{ r: 4 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default SparePartUsageTrendWidget;
